import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Star } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/admin/products")
            .then((r) => setProducts(r.data))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/admin/products/${id}`);
            toast.success("Product deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    };

    return (
        <div data-testid="admin-products" className="space-y-6">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                        Products
                    </h1>
                    <p className="text-sm text-forest-900/70 mt-1">
                        Manage your catalog. Let AI do the heavy lifting.
                    </p>
                </div>
                <Link
                    to="/admin/products/new"
                    data-testid="new-product-btn"
                    className="inline-flex items-center gap-2 bg-forest-800 text-gold-400 font-bold text-sm px-5 py-3 rounded-xl border border-gold-500/40 hover:bg-forest-900"
                >
                    <Plus className="w-4 h-4" />
                    New Product (AI-assisted)
                </Link>
            </header>

            {loading && (
                <div className="text-sm text-forest-700/60">Loading…</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {products.map((p) => (
                    <div
                        key={p.id}
                        data-testid={`admin-product-${p.slug}`}
                        className="bg-white rounded-2xl border border-forest-100 overflow-hidden shadow-sm"
                    >
                        <div className="aspect-video bg-forest-50 relative overflow-hidden">
                            {p.images?.[0] ? (
                                <img
                                    src={p.images[0]}
                                    onError={(e) =>
                                        (e.currentTarget.src =
                                            "https://placehold.co/600x400/faf6ee/8f6b22?text=No+Image")
                                    }
                                    alt={p.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-forest-700/40 text-xs">
                                    No image
                                </div>
                            )}
                            {p.is_flagship && (
                                <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-forest-800 text-gold-400 text-[10px] font-bold px-2 py-1 rounded-full border border-gold-500/40">
                                    <Star className="w-3 h-3 fill-gold-400" />
                                    Flagship
                                </span>
                            )}
                            <span
                                className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full ${
                                    p.is_active
                                        ? "bg-forest-100 text-forest-800"
                                        : "bg-rose-100 text-rose-800"
                                }`}
                            >
                                {p.is_active ? "Active" : "Hidden"}
                            </span>
                        </div>
                        <div className="p-4 space-y-2">
                            <h3 className="font-serif font-bold text-forest-800 text-base line-clamp-1">
                                {p.name}
                            </h3>
                            <p className="text-xs text-forest-900/60 line-clamp-2">
                                {p.short_description}
                            </p>
                            <div className="pt-2 flex items-center justify-between border-t border-forest-100 text-xs">
                                <span className="font-bold text-forest-800">
                                    {p.bundles?.[0]?.price?.toLocaleString() ||
                                        "—"}{" "}
                                    PKR
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <Link
                                        to={`/product/${p.slug}`}
                                        target="_blank"
                                        className="p-2 rounded-lg hover:bg-forest-50"
                                        aria-label="View"
                                    >
                                        <Eye className="w-4 h-4 text-forest-700" />
                                    </Link>
                                    <Link
                                        to={`/admin/products/${p.id}`}
                                        className="p-2 rounded-lg hover:bg-forest-50"
                                        aria-label="Edit"
                                    >
                                        <Pencil className="w-4 h-4 text-forest-700" />
                                    </Link>
                                    <button
                                        onClick={() => remove(p.id)}
                                        className="p-2 rounded-lg hover:bg-rose-50"
                                        aria-label="Delete"
                                    >
                                        <Trash2 className="w-4 h-4 text-rose-600" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {!loading && products.length === 0 && (
                    <div className="col-span-full text-center text-forest-700/60 text-sm py-16">
                        No products yet. Click "New Product (AI-assisted)" to
                        add your first one.
                    </div>
                )}
            </div>
        </div>
    );
}
